/**
 * Returning-customer contact token — client half (#66 A3.5).
 *
 * The token is minted by the edge when a verification lands and pre-attests
 * the NEXT draft server-side: this module only stores/presents it, it never
 * reads contact data out of it. Dual persistence (cookie set by the server +
 * localStorage) — the GDID pattern: whichever store survives revives the
 * other, so an ITP cookie purge or a storage wipe alone doesn't log the
 * customer out.
 *
 * The GDID is the vitrina-DOMAIN device id (48 hex): per-domain storage, it
 * never travels to loginsalon (VERIFICATION_SERVICE.md §5). It rides the
 * X-Vitrina-GDID header on edge calls — the ingredient the token's binding is
 * recomputed against, and the id the edge's rate limits count on.
 *
 * "Cerrar Sesión" (René): clears the token (cookie + localStorage) and ONLY
 * the token — the GDID survives, device identity outlives the session.
 */

import { getVitrinaApiBase } from '@/js/utils/environment';

const TOKEN_KEY = 'salon_vct';
const GDID_KEY = 'GDID';

const base = () => getVitrinaApiBase();

const isHex48 = (value) => /^[0-9a-f]{48}$/.test(value || '');

/** The device id — minted here on first need, then persisted. */
export function ensureGdid() {
  try {
    let gdid = localStorage.getItem(GDID_KEY);
    if (!isHex48(gdid)) {
      const bytes = new Uint8Array(24);
      crypto.getRandomValues(bytes);
      gdid = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(GDID_KEY, gdid);
    }
    return gdid;
  } catch {
    return null; // storage disabled: no device id, no returning token — fine
  }
}

/** Header set for edge calls that participate in the token flow. */
export function gdidHeader() {
  const gdid = ensureGdid();
  return gdid ? { 'X-Vitrina-GDID': gdid } : {};
}

const readCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)salon_vct=([^;]+)/);
  return match && match[1] !== 'deleted' ? match[1] : null;
};

/** The stored token — reviving whichever store lost it. */
export function recallContactToken() {
  try {
    const fromCookie = readCookie();
    const fromStorage = localStorage.getItem(TOKEN_KEY);
    if (fromCookie && !fromStorage) localStorage.setItem(TOKEN_KEY, fromCookie);
    if (!fromCookie && fromStorage) {
      document.cookie = `${TOKEN_KEY}=${fromStorage}; Max-Age=${180 * 86400}; Path=/; Secure; SameSite=Lax`;
    }
    return fromCookie || fromStorage || null;
  } catch {
    return null;
  }
}

export const hasContactToken = () => !!recallContactToken();

/**
 * The stored token's claims — DISPLAY ONLY, unverified (the edge is the
 * authority; it re-validates signature + binding on every use). Used to show
 * the customer their own verified phone/email in full (René 2026-08-11: the
 * fixed panel must show the real number, country code and all — it is the
 * customer's own data, and the token in this storage already carries it).
 */
export function readTokenClaims() {
  const token = recallContactToken();
  if (!token) return null;
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

/** Ask the edge for the token once a verification landed on this draft. */
export async function mintContactToken(tempId) {
  try {
    // The PRIOR token travels along (body + cookie): the edge validates it
    // and MERGES — the new token is the union of old and fresh channels
    // (René's design; a re-mint can never downgrade what was verified).
    const prior = recallContactToken();
    const response = await fetch(
      `${base()}/api/booking/temp/${tempId}/contact-token`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...gdidHeader() },
        body: JSON.stringify(prior ? { prior_token: prior } : {}),
      }
    );
    if (!response.ok) return null;
    const data = await response.json();
    if (data?.token) {
      try {
        localStorage.setItem(TOKEN_KEY, data.token);
      } catch {
        /* cookie (set by the server) still carries it */
      }
      // BOTH stores, client-side too (René's QA, 2026-08-21): on the real
      // domain the response's Set-Cookie updates the cookie (same-origin),
      // but on localhost the edge is CROSS-ORIGIN — the Set-Cookie is
      // ignored, and recallContactToken's deliberate cookie-first order then
      // let a stale revived cookie shadow the freshly minted token forever
      // (the menu kept saying SIN VERIFICAR after a successful proof). Same
      // write shape as the revival belt.
      document.cookie = `${TOKEN_KEY}=${data.token}; Max-Age=${180 * 86400}; Path=/; Secure; SameSite=Lax`;
      return data.token;
    }
    return null;
  } catch (error) {
    console.error('[contactToken] mint failed:', error);
    return null;
  }
}

/**
 * "Borrar datos en este dispositivo" (#114, René's spec verbatim): hold the
 * GDID in memory, clear ALL localStorage and cookies, write ONLY the GDID
 * back. Everything else — token, prefill, profile name, consent flags — is
 * gone; the device identity outlives the wipe (it is the edge's rate-limit
 * and binding ingredient, and re-minting one would just create a second
 * device in the counters).
 *
 * Lives here because this module owns the GDID doctrine. The caller decides
 * what happens after (the menu reloads the page so every module re-reads the
 * now-empty state).
 */
export function wipeDeviceData() {
  let gdid = null;
  try {
    gdid = localStorage.getItem(GDID_KEY);
  } catch {
    /* storage blocked — nothing to preserve */
  }
  try {
    localStorage.clear();
  } catch {
    /* storage blocked — cookies still get cleared below */
  }
  // Expire every cookie readable on this origin, on the paths this app sets
  // them (root; deeper-path or HttpOnly cookies are invisible to JS — the
  // server logout below is the belt for the session cookie, Codex on #114).
  for (const chunk of document.cookie.split(';')) {
    const name = chunk.split('=')[0].trim();
    if (!name) continue;
    document.cookie = `${name}=; Max-Age=0; Path=/`;
    document.cookie = `${name}=; Max-Age=0; Path=/; Secure; SameSite=Lax`;
  }
  // Best-effort server-side clear of the canonical session cookie —
  // keepalive so it survives the reload the caller does next.
  try {
    fetch(`${base()}/api/contact-token/logout`, { method: 'POST', keepalive: true });
  } catch {
    /* the local clears above already removed what JS can see */
  }
  try {
    if (isHex48(gdid)) localStorage.setItem(GDID_KEY, gdid);
  } catch {
    /* storage blocked — ensureGdid mints a fresh one next time */
  }
}

/**
 * Drop the token this browser holds — locally, synchronously, token only.
 *
 * The edge already knows the session is gone when this is called (#136: a
 * `no-session` 401 on my-bookings means the token maps to nothing there), so
 * there is no logout to send — just a dead credential to stop presenting.
 * Never the GDID: the device identity outlives any one session.
 */
export function forgetContactToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* no-op */
  }
  document.cookie = `${TOKEN_KEY}=deleted; Max-Age=0; Path=/; Secure; SameSite=Lax`;
}

/** "Cerrar Sesión": token only — never the GDID. */
export async function logoutContactToken() {
  forgetContactToken();
  try {
    await fetch(`${base()}/api/contact-token/logout`, { method: 'POST' });
  } catch {
    // The local clears above already logged this browser out.
  }
}
